/**
 * Client Supabase singleton pour l'application
 *
 * Responsabilités:
 * - Initialise et exporte l'instance unique du client Supabase typé
 * - Configure la connexion à la base de données via les variables d'environnement
 * - Fournit l'accès centralisé à toutes les fonctionnalités Supabase (auth, database, storage)
 * - Assure la type-safety sur tous les appels `.from()` grâce au générique Database
 *
 * Ne gère pas:
 * - La logique métier (déléguée aux services)
 * - La gestion des erreurs (gérée par les composants/services appelants)
 * - La validation des variables d'environnement (doit être configurée avant l'import)
 *
 * Dépendances: @supabase/supabase-js, types Database, variables d'environnement
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? import.meta.env.VITE_SUPAURL
const supabaseKey =
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ??
  import.meta.env.VITE_SUPABASE_ANON_KEY ??
  import.meta.env.VITE_SUPAKEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    [
      'Configuration Supabase manquante.',
      'Renseignez VITE_SUPABASE_URL et VITE_SUPABASE_PUBLISHABLE_KEY dans votre fichier .env.local.',
      'Alias encore acceptes: VITE_SUPAURL, VITE_SUPABASE_ANON_KEY et VITE_SUPAKEY.',
    ].join(' '),
  )
}

export const supabase: SupabaseClient<Database> = createClient<Database>(supabaseUrl, supabaseKey)
